"use client";

import { Breadcrumbs } from "@/components/breadcrumbs/breadcrumbs";
import { useAppContext } from "@/contexts/app-context";
import { ResponsiveNav } from "@/components/nav-menu/responsive-nav";
import { TabNavigation } from "@/components/nav-menu/tab-navigation";
import * as React from "react";
import { LoginButton } from "@/app/[locale]/(auth)/signin/_components/login-button";
import { usePathname } from "next/navigation";
import { LanguageSwitcher } from "@/components/language-switcher";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "@/i18n/navigation";
import DonistaWordmark from "@/components/icons/donista-wordmark";
import { InstallCtaButton } from "@/components/nav-menu/install-cta-button";

export default function Header() {
  const { user, loading } = useAppContext();
  const pathname = usePathname();
  const isAdmin = pathname.startsWith("/admin");
  const [hasMounted, setHasMounted] = React.useState(false);
  // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time mount: must differ from SSR to show correct skeleton
  React.useEffect(() => setHasMounted(true), []);

  if (loading) {
    const hasSession = hasMounted && document.cookie.includes("__session=");
    return <HeaderSkeleton authenticated={hasSession} />;
  }

  // Authenticated users: tab navigation
  if (user) {
    return (
      <>
        <TabNavigation />
        {isAdmin && (
          <div className="flex h-12 shrink-0 items-center gap-2 px-4 sm:px-6 border-b bg-background/50">
            <Breadcrumbs />
          </div>
        )}
      </>
    );
  }

  // Logged-out: marketing nav. Order: language toggle (leftmost utility) → install CTA → login.
  const rightSlot = (
    <div className="flex items-center gap-2">
      <LanguageSwitcher />
      <InstallCtaButton />
      <LoginButton className="rounded-xl" />
    </div>
  );

  return (
    <>
      <ResponsiveNav rightSlot={rightSlot} />
      {/* Mobile header row — wordmark + login only; the install CTA + long login
          label can't both fit on phone widths, and logged-out mobile users get the
          install CTA prominently via the in-page App/Extension promo + hero. */}
      <div className="md:hidden flex h-14 shrink-0 items-center justify-between gap-2 px-4 sticky top-0 z-40 bg-background border-b">
        <Link href="/">
          <DonistaWordmark priority />
        </Link>
        <LoginButton className="rounded-xl" />
      </div>
    </>
  );
}

export function HeaderSkeleton({ authenticated = false }: { authenticated?: boolean }) {
  if (authenticated) {
    return (
      <>
        {/* Desktop: TabNavigation style */}
        <header className="hidden md:flex items-center h-16 w-full border-b bg-background px-4 md:px-6">
          <div className="grid grid-cols-[auto_1fr_auto] items-center w-full max-w-7xl mx-auto">
            <Skeleton className="w-28 h-7 rounded" />
            <div className="flex items-center justify-center gap-2">
              <Skeleton className="w-16 h-4" />
              <Skeleton className="w-16 h-4" />
              <Skeleton className="w-16 h-4" />
              <Skeleton className="w-16 h-4" />
            </div>
            <div className="flex items-center justify-end gap-2">
              <Skeleton className="w-32 h-9 rounded-xl" />
              <Skeleton className="w-12 h-4" />
            </div>
          </div>
        </header>

        {/* Mobile: slim top bar (wordmark + install CTA) */}
        <div className="md:hidden flex h-14 shrink-0 items-center justify-between gap-2 px-4 sticky top-0 z-40 bg-background border-b">
          <Skeleton className="w-28 h-7 rounded" />
          <Skeleton className="w-28 h-9 rounded-xl" />
        </div>

        {/* Mobile: bottom bar */}
        <nav className="md:hidden fixed bottom-0 left-0 z-50 w-full h-16 border-t bg-background">
          <div className="grid grid-cols-4 h-full">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex flex-col items-center justify-center gap-1">
                <Skeleton className="h-5 w-5 rounded" />
                <Skeleton className="h-3 w-8" />
              </div>
            ))}
          </div>
        </nav>
      </>
    );
  }

  return (
    <>
      {/* Desktop: ResponsiveNav style */}
      <div className="hidden md:block w-full border-b bg-background">
        <div className="w-full max-w-7xl mx-auto h-16 flex items-center">
          <Skeleton className="w-28 h-7 rounded shrink-0 mr-4" />
          <div className="flex items-center gap-4">
            <Skeleton className="w-20 h-4" />
            <Skeleton className="w-20 h-4" />
            <Skeleton className="w-20 h-4" />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Skeleton className="w-8 h-8 rounded" />
            <Skeleton className="w-20 h-8 rounded-md" />
          </div>
        </div>
      </div>

      {/* Mobile: top bar (logo + login) */}
      <div className="md:hidden flex h-14 shrink-0 items-center justify-between px-4 sticky top-0 z-40 bg-background border-b">
        <Skeleton className="w-28 h-7 rounded" />
        <Skeleton className="w-20 h-8 rounded-md" />
      </div>

      {/* Mobile: bottom bar */}
      <nav className="md:hidden fixed bottom-0 left-0 z-50 w-full h-16 border-t bg-background">
        <div className="grid grid-cols-4 h-full">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex flex-col items-center justify-center gap-1">
              <Skeleton className="h-5 w-5 rounded" />
              <Skeleton className="h-3 w-8" />
            </div>
          ))}
        </div>
      </nav>
    </>
  );
}
